import React from 'react';
import { Lock } from 'lucide-react';
import { RankingVisualMode } from 'types/ranking';
import './styles.scss';

interface RankingLayoutSelectorProps {
  selectedLayouts: RankingVisualMode[]; // Array de layouts (mín 1, máx 3)
  onLayoutsChange: (layouts: RankingVisualMode[]) => void;
  maxSelection?: number; // Máximo de seleções permitidas (padrão: 3)
}

const RankingLayoutSelector: React.FC<RankingLayoutSelectorProps> = ({
  selectedLayouts,
  onLayoutsChange,
  maxSelection = 3,
}) => {
  const layouts: { id: RankingVisualMode; name: string; image: string; description?: string }[] = [
    { id: 'podio', name: 'Pódio', image: require('assets/img/podio_onboarding.png') },
    { id: 'lista', name: 'Lista', image: require('assets/img/lista_onboarding.png') },
    { id: 'grid', name: 'Grid', image: require('assets/img/top5_onboarding.png') },
  ];

  const toggleLayout = (layoutId: RankingVisualMode) => {
    // Modos exclusivos: privado, medieval, tesouro, maquiagem e tumba (não podem ser combinados com outros)
    const exclusiveModes: RankingVisualMode[] = ['privado', 'medieval', 'tesouro', 'maquiagem', 'tumba'];
    
    // Se clicar em um modo exclusivo (privado, medieval, tesouro, maquiagem ou tumba)
    if (exclusiveModes.includes(layoutId)) {
      if (selectedLayouts.includes(layoutId)) {
        // Se já está selecionado, desmarcar (volta para modo padrão)
        onLayoutsChange(['podio']);
        return;
      }
      // Define apenas o modo exclusivo
      onLayoutsChange([layoutId]);
      return;
    }

    // Se já está em modo exclusivo (privado, medieval, tesouro, maquiagem ou tumba), NÃO PERMITE clicar em outros modos
    const hasExclusiveMode = selectedLayouts.some(mode => exclusiveModes.includes(mode));
    if (hasExclusiveMode) {
      const exclusiveModeName = selectedLayouts.includes('privado') 
        ? 'Privado' 
        : selectedLayouts.includes('medieval')
        ? 'Pódio Medieval'
        : selectedLayouts.includes('tesouro')
        ? 'Pódio do Tesouro'
        : selectedLayouts.includes('maquiagem')
        ? 'Pódio de Maquiagem'
        : 'Pódio da Tumba';
      alert(`Para selecionar outro modo, desmarque o modo ${exclusiveModeName} primeiro.`);
      return;
    }

    // Lógica normal para outros modos (podio, lista, grid)
    const isSelected = selectedLayouts.includes(layoutId);
    
    if (isSelected) {
      // Remove se já estiver selecionado (mas precisa ter pelo menos 1)
      const newLayouts = selectedLayouts.filter(id => id !== layoutId);
      if (newLayouts.length === 0) {
        alert('Você precisa selecionar pelo menos um modo de visualização.');
        return;
      }
      onLayoutsChange(newLayouts);
    } else {
      // Adiciona se não atingiu o máximo
      if (selectedLayouts.length >= maxSelection) {
        alert(`Você pode selecionar no máximo ${maxSelection} modelos visuais.`);
        return;
      }
      onLayoutsChange([...selectedLayouts, layoutId]);
    }
  };

  const isPrivateActive = selectedLayouts.includes('privado');
  const isMedievalActive = selectedLayouts.includes('medieval');
  const isTreasureActive = selectedLayouts.includes('tesouro');
  const isMakeupActive = selectedLayouts.includes('maquiagem');
  const isTombActive = selectedLayouts.includes('tumba');
  const isExclusiveModeActive = isPrivateActive || isMedievalActive || isTreasureActive || isMakeupActive || isTombActive;

  return (
    <div className="ranking-layout-selector">
      <p className="selection-hint">
        {isPrivateActive ? (
          <>
            🔒 <strong>Modo Privado ativo</strong> - Apenas você poderá visualizar este ranking
          </>
        ) : isMedievalActive ? (
          <>
            ⚔️ <strong>Modo Medieval ativo</strong> - Pódio épico com bandeiras medievais
          </>
        ) : isTreasureActive ? (
          <>
            💎 <strong>Modo Tesouro ativo</strong> - Pódio luxuoso com placas de tesouro
          </>
        ) : isMakeupActive ? (
          <>
            💄 <strong>Modo Maquiagem ativo</strong> - Pódio elegante com tema de beleza
          </>
        ) : isTombActive ? (
          <>
            🏺 <strong>Modo Tumba ativo</strong> - Pódio misterioso com múmias egípcias
          </>
        ) : (
          <>
            Selecione de 1 a {maxSelection} modelos visuais. 
            Atualmente: <strong>{selectedLayouts.length} selecionado(s)</strong>
          </>
        )}
      </p>
      
      <div className="layout-options">
        {layouts.map((layout) => {
          const isSelected = selectedLayouts.includes(layout.id);
          const isDisabled = isExclusiveModeActive; // Desabilita outros se modo exclusivo está ativo
          
          return (
            <div
              key={layout.id}
              className={`layout-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && toggleLayout(layout.id)}
            >
              <div className="layout-image">
                <img src={layout.image} alt={layout.name} />
              </div>
              <div className="layout-info">
                <div className={`layout-checkbox ${isSelected ? 'checked' : ''}`}>
                  {isSelected && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span className="layout-name">{layout.name}</span>
                {layout.description && (
                  <span className="layout-description">{layout.description}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="private-layout-section">
        <p className="private-text">Ou escolha um modo exclusivo</p>
        <div
          className={`private-layout-card medieval-card ${selectedLayouts.includes('medieval') ? 'selected' : ''}`}
          onClick={() => toggleLayout('medieval')}
        >
          <div className="medieval-badge-corner">✨ NOVO</div>
          <div className="private-icon medieval-icon">
            ⚔️
          </div>
          <div className="private-info">
            <h4 className="private-title">Pódio Medieval</h4>
            <p className="private-description">
              Visual épico com bandeiras medievais no estilo Game of Thrones.
            </p>
          </div>
          <div className={`layout-checkbox ${selectedLayouts.includes('medieval') ? 'checked' : ''}`}>
            {selectedLayouts.includes('medieval') && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path
                  d="M1 5L4.5 8.5L11 1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
        <div
          className={`private-layout-card treasure-card ${selectedLayouts.includes('tesouro') ? 'selected' : ''}`}
          onClick={() => toggleLayout('tesouro')}
        >
          <div className="treasure-badge-corner">✨ NOVO</div>
          <div className="private-icon treasure-icon">
            💎
          </div>
          <div className="private-info">
            <h4 className="private-title">Pódio do Tesouro</h4>
            <p className="private-description">
              Visual luxuoso com placas de tesouro douradas e cintilantes.
            </p>
          </div>
          <div className={`layout-checkbox ${selectedLayouts.includes('tesouro') ? 'checked' : ''}`}>
            {selectedLayouts.includes('tesouro') && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path
                  d="M1 5L4.5 8.5L11 1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
        <div
          className={`private-layout-card makeup-card ${selectedLayouts.includes('maquiagem') ? 'selected' : ''}`}
          onClick={() => toggleLayout('maquiagem')}
        >
          <div className="makeup-badge-corner">✨ NOVO</div>
          <div className="private-icon makeup-icon">
            💄
          </div>
          <div className="private-info">
            <h4 className="private-title">Pódio de Maquiagem</h4>
            <p className="private-description">
              Visual elegante com pódio e tema de beleza e cosméticos.
            </p>
          </div>
          <div className={`layout-checkbox ${selectedLayouts.includes('maquiagem') ? 'checked' : ''}`}>
            {selectedLayouts.includes('maquiagem') && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path
                  d="M1 5L4.5 8.5L11 1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
        <div
          className={`private-layout-card tomb-card ${selectedLayouts.includes('tumba') ? 'selected' : ''}`}
          onClick={() => toggleLayout('tumba')}
        >
          <div className="tomb-badge-corner">✨ NOVO</div>
          <div className="private-icon tomb-icon">
            🏺
          </div>
          <div className="private-info">
            <h4 className="private-title">Pódio da Tumba</h4>
            <p className="private-description">
              Ranking misterioso com múmias egípcias em sarcófagos dourados, prateados e bronze.
            </p>
          </div>
          <div className={`layout-checkbox ${selectedLayouts.includes('tumba') ? 'checked' : ''}`}>
            {selectedLayouts.includes('tumba') && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path
                  d="M1 5L4.5 8.5L11 1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
        <div
          className={`private-layout-card ${selectedLayouts.includes('privado') ? 'selected' : ''}`}
          onClick={() => toggleLayout('privado')}
        >
          <div className="private-icon">
            <Lock size={32} />
          </div>
          <div className="private-info">
            <h4 className="private-title">Ranking privado</h4>
            <p className="private-description">
              Somente você poderá visualizar esse ranking.
            </p>
          </div>
          <div className={`layout-checkbox ${selectedLayouts.includes('privado') ? 'checked' : ''}`}>
            {selectedLayouts.includes('privado') && (
              <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                <path
                  d="M1 5L4.5 8.5L11 1.5"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingLayoutSelector;
